<h2 class="text-xl font-semibold mb-4">LYFIS Testing</h2>

<nz-tabset>
  <nz-tab nzTitle="Manual DRS Tester">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="space-y-4">
        <nz-card nzTitle="Configuration" [nzBordered]="true">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium mb-2"
                >Partner Profile</label
              >
              <nz-select
                nzShowSearch
                nzAllowClear
                [(ngModel)]="manualPartnerProfileId"
                nzPlaceHolder="Select partner profile"
                class="w-full"
                (ngModelChange)="onPartnerProfileChange()"
              >
                <nz-option
                  *ngFor="let p of partnerProfiles"
                  [nzValue]="p.id"
                  [nzLabel]="p.name || p.id"
                ></nz-option>
              </nz-select>
            </div>

            <div>
              <label class="block text-sm font-medium mb-2">Disease</label>
              <nz-select
                nzShowSearch
                nzAllowClear
                [(ngModel)]="manualDiseaseId"
                nzPlaceHolder="Select disease"
                class="w-full"
                (ngModelChange)="onDiseaseChange()"
              >
                <nz-option
                  *ngFor="let d of filteredDiseases"
                  [nzValue]="d.id"
                  [nzLabel]="d.data || d.id"
                ></nz-option>
              </nz-select>
            </div>
          </div>
        </nz-card>

        <nz-card nzTitle="Questionnaire Facts" [nzBordered]="true">
          <div class="space-y-3">
            <p *ngIf="!factRows?.length" class="text-gray-500 text-sm italic">
              Select partner profile + disease to load required facts.
            </p>
            <div
              *ngFor="let row of factRows"
              class="grid grid-cols-6 gap-2 items-start"
            >
              <div class="col-span-2">
                <input
                  nz-input
                  [ngModel]="row.key"
                  disabled
                  [title]="row.description || row.key"
                />
                <div *ngIf="row.answerType" class="text-xs text-gray-500 mt-1">
                  {{ row.answerType }}
                </div>
              </div>

              <div class="col-span-3">
                <!-- OPTIONS -->
                <nz-select
                  *ngIf="row.answerType === LyfisAnswerType.OPTIONS"
                  [(ngModel)]="row.value"
                  nzShowSearch
                  nzAllowClear
                  nzPlaceHolder="Select option (uses optionId)"
                  class="w-full"
                >
                  <nz-option
                    *ngFor="let opt of row.availableOptions || []"
                    [nzValue]="opt.key"
                    [nzLabel]="opt.label + ' (' + opt.key + ')'"
                  ></nz-option>
                </nz-select>

                <!-- YES/NO -->
                <nz-select
                  *ngIf="row.answerType === LyfisAnswerType.YES_NO"
                  [(ngModel)]="row.value"
                  nzAllowClear
                  nzPlaceHolder="Select yes/no (uses optionId)"
                  class="w-full"
                >
                  <ng-container
                    *ngIf="
                      (row.availableOptions || []).length;
                      else defaultYesNo
                    "
                  >
                    <nz-option
                      *ngFor="let opt of row.availableOptions || []"
                      [nzValue]="opt.key"
                      [nzLabel]="opt.label + ' (' + opt.key + ')'"
                    ></nz-option>
                  </ng-container>
                  <ng-template #defaultYesNo>
                    <nz-option nzValue="yes" nzLabel="Yes (yes)"></nz-option>
                    <nz-option nzValue="no" nzLabel="No (no)"></nz-option>
                  </ng-template>
                </nz-select>

                <!-- NUMERIC -->
                <input
                  *ngIf="row.answerType === LyfisAnswerType.NUMERIC"
                  nz-input
                  type="number"
                  [(ngModel)]="row.value"
                  [placeholder]="row.placeholder || 'Number'"
                  [attr.min]="row.validation?.min ?? null"
                  [attr.max]="row.validation?.max ?? null"
                />

                <!-- DATE -->
                <input
                  *ngIf="row.answerType === LyfisAnswerType.DATE"
                  nz-input
                  type="date"
                  [(ngModel)]="row.value"
                />

                <!-- ARRAY -->
                <textarea
                  *ngIf="row.answerType === LyfisAnswerType.ARRAY"
                  nz-input
                  rows="2"
                  class="w-full text-sm font-mono"
                  [(ngModel)]="row.value"
                  [placeholder]="
                    row.placeholder || 'Enter JSON array (e.g. [1,2,3])'
                  "
                ></textarea>

                <!-- DERIVED (read-only) -->
                <input
                  *ngIf="row.answerType === LyfisAnswerType.DERIVED"
                  nz-input
                  disabled
                  [ngModel]="'Derived (auto-calculated)'"
                />

                <!-- TEXT/DEFAULT -->
                <input
                  *ngIf="
                    !row.answerType ||
                    row.answerType === LyfisAnswerType.TEXT_BOX
                  "
                  nz-input
                  [(ngModel)]="row.value"
                  [placeholder]="row.placeholder || 'Value'"
                />

                <!-- Validation hints -->
                <div
                  *ngIf="
                    row.validation?.min !== undefined ||
                    row.validation?.max !== undefined
                  "
                  class="text-xs text-gray-500 mt-1"
                >
                  <span *ngIf="row.validation?.min !== undefined">
                    min: {{ row.validation?.min }}
                  </span>
                  <span *ngIf="row.validation?.max !== undefined" class="ml-2">
                    max: {{ row.validation?.max }}
                  </span>
                </div>
                <div
                  *ngIf="row.validation?.regex"
                  class="text-xs text-gray-500"
                >
                  regex: {{ row.validation?.regex }}
                </div>
              </div>

              <span class="col-span-1"></span>
            </div>
          </div>
        </nz-card>

        <nz-card nzTitle="Lab Markers" [nzBordered]="true">
          <div class="space-y-3">
            <p *ngIf="!labRows?.length" class="text-gray-500 text-sm italic">
              Select partner profile + disease to load required lab markers.
            </p>
            <div
              *ngFor="let row of labRows"
              class="grid grid-cols-6 gap-2 items-center"
            >
              <input
                nz-input
                [ngModel]="row.markerKey"
                disabled
                class="col-span-2"
              />
              <input
                nz-input
                type="number"
                [(ngModel)]="row.value"
                placeholder="Value"
              />
              <input
                nz-input
                type="datetime-local"
                [(ngModel)]="row.reportDate"
              />
              <span></span>
            </div>
          </div>
        </nz-card>

        <nz-card [nzBordered]="true">
          <button
            nz-button
            nzType="primary"
            nzSize="large"
            [nzLoading]="manualLoading"
            (click)="runManualPreview()"
            class="w-full"
          >
            <span nz-icon nzType="play-circle" nzTheme="outline"></span>
            <span class="ml-2">Run Preview</span>
          </button>
        </nz-card>
      </div>
      <div>
        <nz-card nzTitle="Preview Output" [nzBordered]="true">
          <div *ngIf="manualPreviewFields as f" class="space-y-4">
            <div class="flex items-start justify-between gap-3 pb-3 border-b">
              <div class="flex items-center gap-2">
                <span class="text-gray-600 text-sm font-medium">Band</span>
                <nz-tag [nzColor]="getBandColor(f.band)" class="text-sm">
                  {{ toDisplayString(f.band) }}
                </nz-tag>
              </div>

              <div class="flex items-center gap-2 text-right">
                <span class="text-gray-500 text-xs">rulesVersionId</span>
                <span
                  class="font-mono text-xs bg-gray-100 px-2 py-1 rounded"
                  [title]="toDisplayString(f.rulesVersionId)"
                >
                  {{ shortenMiddle(f.rulesVersionId, 12, 10) }}
                </span>
                <button
                  nz-button
                  nzSize="small"
                  nzType="default"
                  nzShape="circle"
                  (click)="copyToClipboard(f.rulesVersionId)"
                >
                  <span nz-icon nzType="copy" nzTheme="outline"></span>
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div
                class="border rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-4 shadow-sm"
              >
                <div class="text-xs text-gray-600 mb-2 font-medium">
                  Questionnaire
                </div>
                <div class="text-3xl font-bold font-mono text-blue-700">
                  {{ toDisplayString(f.questionnaireScore) }}
                </div>
              </div>
              <div
                class="border rounded-lg bg-gradient-to-br from-green-50 to-green-100 p-4 shadow-sm"
              >
                <div class="text-xs text-gray-600 mb-2 font-medium">
                  Lab markers
                </div>
                <div class="text-3xl font-bold font-mono text-green-700">
                  {{ toDisplayString(f.labMarkersScore) }}
                </div>
              </div>
              <div
                class="border rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 p-4 shadow-sm"
              >
                <div class="text-xs text-gray-600 mb-2 font-medium">DRS</div>
                <div class="text-3xl font-bold font-mono text-purple-700">
                  {{ toDisplayString(f.drsScore) }}
                </div>
              </div>
              <div
                class="border rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 p-4 shadow-sm"
              >
                <div class="text-xs text-gray-600 mb-2 font-medium">PDH</div>
                <div class="text-3xl font-bold font-mono text-orange-700">
                  {{ toDisplayString(f.pdhScore) }}
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3 pt-3 border-t">
              <span class="text-gray-600 text-sm font-medium">Weights</span>
              <span
                class="px-3 py-1.5 rounded-md border bg-white font-mono text-sm shadow-sm"
              >
                wQ: {{ toDisplayString(f.wQ) }}
              </span>
              <span
                class="px-3 py-1.5 rounded-md border bg-white font-mono text-sm shadow-sm"
              >
                wL: {{ toDisplayString(f.wL) }}
              </span>
            </div>

            <!-- Score Breakdown Section -->
            <div class="mt-6" *ngIf="manualResultTrace">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700"
                  >Score Breakdown (Trace)</span
                >
                <button
                  nz-button
                  nzType="primary"
                  nzSize="small"
                  (click)="openManualTraceInNewTab()"
                >
                  <span nz-icon nzType="fullscreen" nzTheme="outline"></span>
                  Open Full Page
                </button>
              </div>
              <nz-collapse>
                <nz-collapse-panel
                  nzHeader="Expand Inline Preview"
                  [nzActive]="false"
                >
                  <div class="p-4 space-y-6">
                    <!-- Totals Summary Cards -->
                    <div class="grid grid-cols-2 gap-3">
                      <div
                        class="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"
                      >
                        <div
                          class="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1"
                        >
                          Questionnaire Raw
                        </div>
                        <div class="text-xl font-bold text-purple-700">
                          {{ manualResultTrace.totals.rawQuestionnairePoints }}
                        </div>
                      </div>
                      <div
                        class="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"
                      >
                        <div
                          class="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1"
                        >
                          Questionnaire Max
                        </div>
                        <div class="text-xl font-bold text-purple-700">
                          {{ manualResultTrace.totals.maxQuestionnairePoints }}
                        </div>
                      </div>
                      <div
                        class="bg-green-50 border border-green-200 rounded-lg p-3 text-center"
                      >
                        <div
                          class="text-xs text-green-600 font-medium uppercase tracking-wide mb-1"
                        >
                          Lab Raw
                        </div>
                        <div class="text-xl font-bold text-green-700">
                          {{ manualResultTrace.totals.rawLabPoints }}
                        </div>
                      </div>
                      <div
                        class="bg-green-50 border border-green-200 rounded-lg p-3 text-center"
                      >
                        <div
                          class="text-xs text-green-600 font-medium uppercase tracking-wide mb-1"
                        >
                          Lab Max
                        </div>
                        <div class="text-xl font-bold text-green-700">
                          {{ manualResultTrace.totals.maxLabPoints }}
                        </div>
                      </div>
                    </div>

                    <!-- Scenario Selection -->
                    <div
                      class="bg-blue-50 border border-blue-200 rounded-lg p-4"
                      *ngIf="manualResultTrace.scenarioTrace"
                    >
                      <h4
                        class="text-base font-semibold text-blue-800 mb-3 flex items-center gap-2"
                      >
                        <span
                          nz-icon
                          nzType="branches"
                          nzTheme="outline"
                        ></span>
                        Scenario Selection
                      </h4>
                      <div
                        class="bg-white rounded-lg p-3 mb-4 border border-blue-100"
                      >
                        <div class="flex flex-wrap items-center gap-3">
                          <div>
                            <span class="text-sm text-gray-500">Selected:</span>
                            <span class="ml-2 font-semibold text-blue-700">{{
                              manualResultTrace.scenarioTrace.selectedScenario
                            }}</span>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="px-2 py-1 bg-purple-100 rounded">
                              <span class="text-xs text-purple-600">wQ:</span>
                              <span
                                class="ml-1 font-mono font-semibold text-purple-700"
                                >{{
                                  manualResultTrace.scenarioTrace.weights.wQ
                                }}</span
                              >
                            </div>
                            <div class="px-2 py-1 bg-green-100 rounded">
                              <span class="text-xs text-green-600">wL:</span>
                              <span
                                class="ml-1 font-mono font-semibold text-green-700"
                                >{{
                                  manualResultTrace.scenarioTrace.weights.wL
                                }}</span
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                      <nz-table
                        [nzData]="
                          manualResultTrace.scenarioTrace.evaluatedScenarios
                        "
                        [nzShowPagination]="false"
                        nzSize="middle"
                        [nzScroll]="{ y: '180px' }"
                      >
                        <thead>
                          <tr>
                            <th nzWidth="180px">Scenario Name</th>
                            <th>Condition</th>
                            <th nzWidth="90px">Matched</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            *ngFor="
                              let s of manualResultTrace.scenarioTrace
                                .evaluatedScenarios
                            "
                            [class.bg-green-100]="s.matched"
                          >
                            <td class="font-mono text-sm">
                              {{ s.scenarioName }}
                            </td>
                            <td class="font-mono text-sm text-gray-600">
                              {{ s.condition }}
                            </td>
                            <td>
                              <nz-tag
                                [nzColor]="s.matched ? 'green' : 'default'"
                                class="text-sm"
                              >
                                {{ s.matched ? "✓ Yes" : "No" }}
                              </nz-tag>
                            </td>
                          </tr>
                        </tbody>
                      </nz-table>
                    </div>

                    <!-- Questionnaire Breakdown -->
                    <div
                      class="bg-purple-50 border border-purple-200 rounded-lg p-4"
                      *ngIf="manualResultTrace.questionnaireBreakdown?.length"
                    >
                      <h4
                        class="text-base font-semibold text-purple-800 mb-3 flex items-center gap-2"
                      >
                        <span nz-icon nzType="form" nzTheme="outline"></span>
                        Questionnaire Breakdown
                      </h4>
                      <nz-table
                        [nzData]="manualResultTrace.questionnaireBreakdown"
                        [nzShowPagination]="false"
                        nzSize="middle"
                        [nzScroll]="{ y: '250px' }"
                      >
                        <thead>
                          <tr>
                            <th nzWidth="140px">Fact Key</th>
                            <th nzWidth="120px">Input Value</th>
                            <th>Evaluated Condition</th>
                            <th nzWidth="90px">Matched</th>
                            <th nzWidth="80px">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            *ngFor="
                              let q of manualResultTrace.questionnaireBreakdown
                            "
                            [class.bg-green-100]="q.matched"
                          >
                            <td class="font-mono text-sm">{{ q.factKey }}</td>
                            <td class="font-mono text-sm">
                              {{ q.inputValue }}
                            </td>
                            <td class="font-mono text-sm text-gray-600">
                              {{ q.evaluatedCondition }}
                            </td>
                            <td>
                              <nz-tag
                                [nzColor]="q.matched ? 'green' : 'default'"
                                class="text-sm"
                              >
                                {{ q.matched ? "✓ Yes" : "No" }}
                              </nz-tag>
                            </td>
                            <td
                              class="font-mono text-sm font-bold"
                              [class.text-green-600]="q.pointsAwarded > 0"
                            >
                              {{ q.pointsAwarded }}
                            </td>
                          </tr>
                        </tbody>
                      </nz-table>
                    </div>

                    <!-- Lab Breakdown -->
                    <div
                      class="bg-green-50 border border-green-200 rounded-lg p-4"
                      *ngIf="manualResultTrace.labBreakdown?.length"
                    >
                      <h4
                        class="text-base font-semibold text-green-800 mb-3 flex items-center gap-2"
                      >
                        <span
                          nz-icon
                          nzType="experiment"
                          nzTheme="outline"
                        ></span>
                        Lab Breakdown
                      </h4>
                      <nz-table
                        [nzData]="manualResultTrace.labBreakdown"
                        [nzShowPagination]="false"
                        nzSize="middle"
                        [nzScroll]="{ y: '250px' }"
                      >
                        <thead>
                          <tr>
                            <th nzWidth="140px">Marker Key</th>
                            <th nzWidth="80px">Value</th>
                            <th nzWidth="90px">Available</th>
                            <th>Evaluated Condition</th>
                            <th nzWidth="90px">Matched</th>
                            <th nzWidth="80px">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            *ngFor="let l of manualResultTrace.labBreakdown"
                            [class.bg-green-100]="l.matched"
                            [class.bg-gray-200]="!l.available"
                          >
                            <td class="font-mono text-sm">{{ l.markerKey }}</td>
                            <td class="font-mono text-sm">
                              {{ l.inputValue !== null ? l.inputValue : "—" }}
                            </td>
                            <td>
                              <nz-tag
                                [nzColor]="l.available ? 'blue' : 'default'"
                                class="text-sm"
                              >
                                {{ l.available ? "✓ Yes" : "No" }}
                              </nz-tag>
                            </td>
                            <td class="font-mono text-sm text-gray-600">
                              {{ l.evaluatedCondition }}
                            </td>
                            <td>
                              <nz-tag
                                [nzColor]="l.matched ? 'green' : 'default'"
                                class="text-sm"
                              >
                                {{ l.matched ? "✓ Yes" : "No" }}
                              </nz-tag>
                            </td>
                            <td
                              class="font-mono text-sm font-bold"
                              [class.text-green-600]="l.pointsAwarded > 0"
                            >
                              {{ l.pointsAwarded }}
                            </td>
                          </tr>
                        </tbody>
                      </nz-table>
                    </div>
                  </div>
                </nz-collapse-panel>
              </nz-collapse>
            </div>

            <div
              *ngIf="manualResult && !manualResultTrace"
              class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"
            >
              <span
                nz-icon
                nzType="warning"
                nzTheme="outline"
                class="mr-1"
              ></span>
              Trace not available. The compiled artifact may need to be
              recompiled to generate trace data.
            </div>
          </div>
          <div *ngIf="!manualResult" class="text-center py-8">
            <span
              nz-icon
              nzType="info-circle"
              nzTheme="outline"
              class="text-4xl text-gray-300 mb-2"
            ></span>
            <p class="text-gray-500 text-sm">
              Run preview to see results here.
            </p>
          </div>
        </nz-card>
      </div>
    </div>
  </nz-tab>

  <nz-tab nzTitle="Bulk Upload Testing">
    <div class="space-y-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <nz-card
          nzTitle="How to Use Bulk Upload"
          [nzBordered]="true"
          class="shadow-sm"
        >
          <div class="bg-blue-50 border-l-4 border-blue-500 rounded p-4">
            <ol
              class="list-decimal list-inside space-y-2 text-sm text-blue-800"
            >
              <li>
                <strong>Select Partner Profile and Disease:</strong> Choose the
                partner profile and disease for which you want to test
                scenarios. This determines which facts and lab markers are
                required.
              </li>
              <li>
                <strong>Export CSV Template:</strong> Click "Export CSV
                template" to download a CSV file with the correct column
                structure. The template includes all required questionnaire
                facts and lab markers for the selected disease.
              </li>
              <li>
                <strong>Fill in Your Test Scenarios:</strong> Open the
                downloaded CSV template and fill in your test data. Each row
                represents one test scenario. You can add multiple scenarios by
                adding multiple rows.
                <ul class="list-disc list-inside ml-4 mt-1 space-y-1 text-xs">
                  <li>
                    Use the <code>scenarioId</code> column to give each scenario
                    a unique identifier (optional)
                  </li>
                  <li>
                    Fill in questionnaire facts using the
                    <code>fact:&lt;factKey&gt;</code> columns
                  </li>
                  <li>
                    Fill in lab markers using the
                    <code>lab:&lt;markerKey&gt;</code> columns
                  </li>
                  <li>
                    Add lab report dates using the
                    <code>labDate:&lt;markerKey&gt;</code> columns (optional,
                    defaults to current date)
                  </li>
                </ul>
              </li>
              <li>
                <strong>Import CSV:</strong> Click "Import CSV" and select your
                filled CSV file. The system will validate and parse your
                scenarios.
              </li>
              <li>
                <strong>Run Bulk Preview:</strong> Once your CSV is imported,
                click "Run bulk preview" to process all scenarios. This will
                calculate scores for each scenario.
              </li>
              <li>
                <strong>Review Results:</strong> Check the results table to see
                scores, bands, and any errors for each scenario. Successful
                scenarios will show DRS, PDH scores and bands.
              </li>
              <li>
                <strong>Export Results (Optional):</strong> Click "Export
                results CSV" to download all results for further analysis.
              </li>
            </ol>
            <div
              class="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded text-xs text-yellow-800"
            >
              <strong>Note:</strong> For options-based facts, use the option key
              (e.g., "yes", "no", or the specific option key). For numeric
              values, enter the number directly. For arrays, use JSON array
              format (e.g., [1,2,3]).
            </div>
          </div>
        </nz-card>

        <div class="space-y-4">
          <nz-card nzTitle="Configuration" [nzBordered]="true">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium mb-2"
                  >Partner Profile</label
                >
                <nz-select
                  nzShowSearch
                  nzAllowClear
                  [(ngModel)]="manualPartnerProfileId"
                  nzPlaceHolder="Select partner profile"
                  class="w-full"
                  (ngModelChange)="onPartnerProfileChange()"
                >
                  <nz-option
                    *ngFor="let p of partnerProfiles"
                    [nzValue]="p.id"
                    [nzLabel]="p.name || p.id"
                  ></nz-option>
                </nz-select>

                <label class="block text-sm font-medium">Disease</label>
                <nz-select
                  nzShowSearch
                  nzAllowClear
                  [(ngModel)]="manualDiseaseId"
                  nzPlaceHolder="Select disease"
                  class="w-full"
                  (ngModelChange)="onDiseaseChange()"
                >
                  <nz-option
                    *ngFor="let d of filteredDiseases"
                    [nzValue]="d.id"
                    [nzLabel]="d.data || d.id"
                  ></nz-option>
                </nz-select>
              </div>
            </div>
          </nz-card>

          <nz-card nzTitle="Actions" [nzBordered]="true">
            <div class="space-y-4">
              <div>
                <button
                  nz-button
                  nzType="default"
                  nzBlock
                  (click)="exportCsvTemplate()"
                  [disabled]="!manualPartnerProfileId || !manualDiseaseId"
                >
                  <span nz-icon nzType="download" nzTheme="outline"></span>
                  <span class="ml-2">Export CSV template</span>
                </button>
              </div>

              <div class="space-y-2">
                <input
                  #bulkCsvInput
                  type="file"
                  accept=".csv,text/csv"
                  class="hidden"
                  (change)="onBulkCsvSelected($event)"
                />
                <button
                  nz-button
                  nzType="default"
                  nzBlock
                  (click)="bulkCsvInput.click()"
                  [disabled]="!manualPartnerProfileId || !manualDiseaseId"
                >
                  <span nz-icon nzType="upload" nzTheme="outline"></span>
                  <span class="ml-2">Import CSV</span>
                </button>
              </div>

              <div class="pt-2 border-t">
                <button
                  nz-button
                  nzType="primary"
                  nzBlock
                  nzSize="large"
                  [nzLoading]="bulkLoading"
                  (click)="runBulkPreview()"
                  [disabled]="!bulkScenarioOrder.length"
                >
                  <span nz-icon nzType="play-circle" nzTheme="outline"></span>
                  <span class="ml-2">Run bulk preview</span>
                </button>
              </div>

              <div>
                <button
                  nz-button
                  nzType="default"
                  nzBlock
                  (click)="exportBulkResultsCsv()"
                  [disabled]="!bulkResults.length"
                >
                  <span nz-icon nzType="file-excel" nzTheme="outline"></span>
                  <span class="ml-2">Export results CSV</span>
                </button>
              </div>

              <div
                class="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border"
                *ngIf="bulkScenarioOrder?.length"
              >
                <span class="font-semibold text-blue-600">{{
                  bulkScenarioOrder.length
                }}</span>
                <span class="ml-1">scenario(s) loaded</span>
              </div>
            </div>
          </nz-card>
        </div>
      </div>

      <nz-card nzTitle="Bulk Results" [nzBordered]="true" class="w-full">
        <div
          class="flex flex-wrap items-center justify-between gap-2 mb-3"
          *ngIf="bulkScenarioOrder?.length"
        >
          <div class="text-sm text-gray-600">
            Showing
            <span class="font-semibold">{{ bulkResultsTableView.length }}</span>
            result(s)
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              nz-button
              nzType="default"
              nzSize="small"
              (click)="resetBulkResultsTableFilters()"
              [disabled]="
                !bulkScenarioSearchActive &&
                !bulkRulesSearchActive &&
                !bulkErrorSearchActive &&
                !bulkStatusFilterActive &&
                !bulkBandFilterActive &&
                !bulkResultsSortOrder
              "
            >
              Clear filter
            </button>
            <button
              nz-button
              nzType="primary"
              nzSize="small"
              (click)="exportBulkResultsCsv()"
              [disabled]="!bulkResultsTableView.length"
            >
              <span nz-icon nzType="download"></span>
              Export table CSV
            </button>
          </div>
        </div>

        <div
          *ngIf="bulkScenarioOrder?.length && bulkResults?.length"
          class="border rounded-lg"
        >
          <nz-table
            #bulkResultsTable
            [nzData]="bulkResultsTableView"
            [nzBordered]="true"
            [nzShowSizeChanger]="true"
            [nzPageSize]="bulkResultsPageSize"
            [nzPageSizeOptions]="bulkResultsPageSizeOptions"
            nzTableLayout="fixed"
            [nzScroll]="{ x: '1600px', y: '60vh' }"
          >
            <thead>
              <tr>
                <th nzWidth="80px">Actions</th>
                <th nzWidth="240px">
                  Scenario
                  <nz-filter-trigger
                    [nzActive]="bulkScenarioSearchActive"
                    [nzDropdownMenu]="bulkScenarioSearchMenu"
                  >
                    <span nz-icon nzType="search"></span>
                  </nz-filter-trigger>
                </th>
                <th nzWidth="110px">
                  Status
                  <nz-filter-trigger
                    [nzActive]="bulkStatusFilterActive"
                    [nzDropdownMenu]="bulkStatusFilterMenu"
                  >
                    <span nz-icon nzType="filter"></span>
                  </nz-filter-trigger>
                </th>
                <th
                  nzWidth="90px"
                  class="text-right"
                  (nzSortOrderChange)="sortBulkResultsTable('drs', $event)"
                  nzShowSort="true"
                >
                  DRS
                </th>
                <th
                  nzWidth="90px"
                  class="text-right"
                  (nzSortOrderChange)="sortBulkResultsTable('pdh', $event)"
                  nzShowSort="true"
                >
                  PDH
                </th>
                <th
                  nzWidth="100px"
                  class="text-right"
                  (nzSortOrderChange)="sortBulkResultsTable('labscore', $event)"
                  nzShowSort="true"
                >
                  Lab Score
                </th>
                <th
                  nzWidth="140px"
                  class="text-right"
                  (nzSortOrderChange)="
                    sortBulkResultsTable('questionnaireScore', $event)
                  "
                  nzShowSort="true"
                >
                  Questionnaire Score
                </th>
                <th
                  nzWidth="80px"
                  class="text-right"
                  (nzSortOrderChange)="sortBulkResultsTable('wq', $event)"
                  nzShowSort="true"
                >
                  wQ
                </th>
                <th
                  nzWidth="80px"
                  class="text-right"
                  (nzSortOrderChange)="sortBulkResultsTable('wlab', $event)"
                  nzShowSort="true"
                >
                  wLab
                </th>
                <th nzWidth="140px">
                  Band
                  <nz-filter-trigger
                    [nzActive]="bulkBandFilterActive"
                    [nzDropdownMenu]="bulkBandFilterMenu"
                  >
                    <span nz-icon nzType="filter"></span>
                  </nz-filter-trigger>
                </th>
                <th nzWidth="360px">
                  rulesVersionId
                  <nz-filter-trigger
                    [nzActive]="bulkRulesSearchActive"
                    [nzDropdownMenu]="bulkRulesSearchMenu"
                  >
                    <span nz-icon nzType="search"></span>
                  </nz-filter-trigger>
                </th>
                <th nzWidth="520px">
                  Error
                  <nz-filter-trigger
                    [nzActive]="bulkErrorSearchActive"
                    [nzDropdownMenu]="bulkErrorSearchMenu"
                  >
                    <span nz-icon nzType="search"></span>
                  </nz-filter-trigger>
                </th>
              </tr>
            </thead>
            <tbody>
              <ng-container
                *ngFor="let r of bulkResultsTable.data; let i = index"
              >
                <tr class="hover:bg-gray-50 transition-colors">
                  <td>
                    <button
                      nz-button
                      nzSize="small"
                      nzType="primary"
                      nz-tooltip
                      nzTooltipTitle="View Score Trace"
                      (click)="openTraceInNewTab(r)"
                      [disabled]="!r.trace"
                    >
                      <span
                        nz-icon
                        nzType="file-search"
                        nzTheme="outline"
                      ></span>
                    </button>
                  </td>
                  <td>
                    <div class="flex items-center gap-2 min-w-0">
                      <span
                        class="font-mono text-xs whitespace-nowrap"
                        [title]="toDisplayString(r.scenarioId)"
                      >
                        {{ shortenMiddle(r.scenarioId, 18, 10) }}
                      </span>
                      <button
                        nz-button
                        nzSize="small"
                        nzType="default"
                        nzShape="circle"
                        (click)="
                          copyToClipboard(r.scenarioId);
                          $event.stopPropagation()
                        "
                        [disabled]="!r.scenarioId || r.scenarioId === '—'"
                      >
                        <span nz-icon nzType="copy" nzTheme="outline"></span>
                      </button>
                    </div>
                  </td>
                  <td>
                    <nz-tag [nzColor]="r.status === 'OK' ? 'green' : 'red'">
                      {{ r.status }}
                    </nz-tag>
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{ r.drs !== null ? toDisplayString(r.drs) : "—" }}
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{ r.pdh !== null ? toDisplayString(r.pdh) : "—" }}
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{
                      r.labscore !== null ? toDisplayString(r.labscore) : "—"
                    }}
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{
                      r.questionnaireScore !== null
                        ? toDisplayString(r.questionnaireScore)
                        : "—"
                    }}
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{ r.wq !== null ? toDisplayString(r.wq) : "—" }}
                  </td>
                  <td class="font-mono tabular-nums text-right">
                    {{ r.wlab !== null ? toDisplayString(r.wlab) : "—" }}
                  </td>
                  <td>
                    {{ r.band !== null ? toDisplayString(r.band) : "—" }}
                  </td>
                  <td>
                    <div class="flex items-center gap-2 min-w-0">
                      <span
                        class="font-mono text-xs whitespace-nowrap bg-gray-100 px-2 py-1 rounded"
                        [title]="toDisplayString(r.rulesVersionId)"
                      >
                        {{ shortenMiddle(r.rulesVersionId, 16, 12) }}
                      </span>
                      <button
                        nz-button
                        nzSize="small"
                        nzType="default"
                        nzShape="circle"
                        (click)="copyToClipboard(r.rulesVersionId)"
                        [disabled]="!r.rulesVersionId"
                      >
                        <span nz-icon nzType="copy" nzTheme="outline"></span>
                      </button>
                    </div>
                  </td>
                  <td
                    class="text-red-600 text-xs whitespace-normal break-words"
                    [title]="toDisplayString(r.error)"
                  >
                    {{ r.error || "—" }}
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </nz-table>
        </div>
        <div
          *ngIf="bulkScenarioOrder?.length && !bulkResults?.length"
          class="text-center py-8"
        >
          <span
            nz-icon
            nzType="hourglass"
            nzTheme="outline"
            class="text-4xl text-gray-300 mb-2"
          ></span>
          <p class="text-gray-500 text-sm">
            Import CSV and run bulk preview to see results here.
          </p>
        </div>
        <div *ngIf="!bulkScenarioOrder?.length" class="text-center py-8">
          <span
            nz-icon
            nzType="file-text"
            nzTheme="outline"
            class="text-4xl text-gray-300 mb-2"
          ></span>
          <p class="text-gray-500 text-sm">
            Import a CSV file to start bulk testing.
          </p>
        </div>
      </nz-card>

      <!-- Bulk Results column search / filters -->
      <nz-dropdown-menu #bulkScenarioSearchMenu="nzDropdownMenu">
        <div class="bg-white drop-shadow-md m-1 p-2 rounded-md">
          <div class="m-1" nz-row>
            <div nz-col>
              <input
                [(ngModel)]="bulkScenarioSearchText"
                nz-input
                placeholder="Search scenario"
                nzSize="small"
              />
            </div>
          </div>
          <div class="m-1" nz-row nzJustify="end">
            <div nz-col>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="bulkResultsFilterOk()"
              >
                Ok
              </button>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="resetBulkResultsTableFilters()"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </nz-dropdown-menu>

      <nz-dropdown-menu #bulkRulesSearchMenu="nzDropdownMenu">
        <div class="bg-white drop-shadow-md m-1 p-2 rounded-md">
          <div class="m-1" nz-row>
            <div nz-col>
              <input
                [(ngModel)]="bulkRulesVersionIdSearchText"
                nz-input
                placeholder="Search rulesVersionId"
                nzSize="small"
              />
            </div>
          </div>
          <div class="m-1" nz-row nzJustify="end">
            <div nz-col>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="bulkResultsFilterOk()"
              >
                Ok
              </button>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="resetBulkResultsTableFilters()"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </nz-dropdown-menu>

      <nz-dropdown-menu #bulkErrorSearchMenu="nzDropdownMenu">
        <div class="bg-white drop-shadow-md m-1 p-2 rounded-md">
          <div class="m-1" nz-row>
            <div nz-col>
              <input
                [(ngModel)]="bulkErrorSearchText"
                nz-input
                placeholder="Search error"
                nzSize="small"
              />
            </div>
          </div>
          <div class="m-1" nz-row nzJustify="end">
            <div nz-col>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="bulkResultsFilterOk()"
              >
                Ok
              </button>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="resetBulkResultsTableFilters()"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </nz-dropdown-menu>

      <nz-dropdown-menu #bulkStatusFilterMenu="nzDropdownMenu">
        <div class="bg-white drop-shadow-md m-1 p-2 rounded-md">
          <div nz-row>
            <div nz-col>
              <span class="font-bold">Status</span>
              <nz-divider></nz-divider>
              <nz-checkbox-wrapper (nzOnChange)="bulkStatusFilter($event)">
                <div *ngFor="let opt of bulkStatusFilterOptions" nz-row>
                  <div nz-col>
                    <label
                      nz-checkbox
                      [nzValue]="opt.value"
                      [ngModel]="opt.checked"
                      >{{ opt.label }}</label
                    >
                  </div>
                </div>
              </nz-checkbox-wrapper>
            </div>
          </div>
          <div nz-row nzJustify="end">
            <div nz-col>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="bulkResultsFilterOk()"
              >
                Ok
              </button>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="resetBulkResultsTableFilters()"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </nz-dropdown-menu>

      <nz-dropdown-menu #bulkBandFilterMenu="nzDropdownMenu">
        <div class="bg-white drop-shadow-md m-1 p-2 rounded-md">
          <div nz-row>
            <div nz-col>
              <span class="font-bold">Band</span>
              <nz-divider></nz-divider>
              <div
                *ngIf="!bulkBandFilterOptions?.length"
                class="text-xs text-gray-500"
              >
                No bands available
              </div>
              <nz-checkbox-wrapper
                *ngIf="bulkBandFilterOptions?.length"
                (nzOnChange)="bulkBandFilter($event)"
              >
                <div *ngFor="let opt of bulkBandFilterOptions" nz-row>
                  <div nz-col>
                    <label
                      nz-checkbox
                      [nzValue]="opt.value"
                      [ngModel]="opt.checked"
                      >{{ opt.label }}</label
                    >
                  </div>
                </div>
              </nz-checkbox-wrapper>
            </div>
          </div>
          <div nz-row nzJustify="end">
            <div nz-col>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="bulkResultsFilterOk()"
              >
                Ok
              </button>
              <button
                class="m-1"
                nz-button
                nzSize="small"
                nzType="primary"
                (click)="resetBulkResultsTableFilters()"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </nz-dropdown-menu>
    </div>
  </nz-tab>
</nz-tabset>
